var _a;
/**
* HttpManager.ts
* @author Abhilash Panwar (abpanwar); Hector Hernandez (hectorh); Nev Wylie (newylie)
* @copyright Microsoft 2018-2020
*/
import { isReactNative, isValueAssigned, isString, getTime, arrForEach, getLocation, strTrim, isBeaconsSupported, FullVersionString, useXDomainRequest, LoggingSeverity, _ExtendedInternalMessageId, strUndefined, getNavigator, doPerf, dateNow, isUndefined, isNullOrUndefined, objForEachKey, } from "@microsoft/1ds-core-js";
import { Serializer } from "./Serializer";
import RetryPolicy from "./RetryPolicy";
import EVTKillSwitch from "./KillSwitch";
import EVTClockSkewManager from "./ClockSkewManager";
import dynamicProto from "@microsoft/dynamicproto-js";
var Method = "POST";
var DisabledPropertyName = "Microsoft_ApplicationInsights_BypassAjaxInstrumentation";
var strDropped = "drop";
var strSending = "send";
var strRequeue = "requeue";
var strResponseFail = "rspFail";
var strOther = "oth";
var strKillTokensHeader = "kill-tokens";
var strKillDurationHeader = "kill-duration";
var strKillDurationSecondsHeader = "kill-duration-seconds";
var strTimeDeltaHeader = "time-delta-millis";
/**
 * Identifies the default notification reason to the action names
 */
var _eventActionMap = (_a = {},
    _a[1 /* Paused */] = strRequeue,
    _a[100 /* RequeueEvents */] = strRequeue,
    _a[200 /* Complete */] = "sent",
    _a[8004 /* KillSwitch */] = strDropped,
    _a[8003 /* SizeLimitExceeded */] = strDropped,
    _a);
function _getResponseText(xhr) {
    try {
        return xhr.responseText;
    }
    catch (e) {
        // Best effort, as XHR may throw while XDR wont so just ignore
    }
    return "";
}
/**
 * Class managing the sending of requests.
 */
var HttpManager = /** @class */ (function () {
    /**
     * @constructor
     * @param requestQueue   - The queue that contains the requests to be sent.
     */
    function HttpManager(maxEventsPerBatch, maxConnections, maxRetries, actions) {
        this._responseHandlers = [];
        var _urlString = "?cors=true&content-type=application/x-json-stream&client-id=NO_AUTH&client-version="
            + FullVersionString;
        var _killSwitch = new EVTKillSwitch();
        var _paused = false;
        var _clockSkewManager = new EVTClockSkewManager();
        var _useBeacons = false;
        var _outstandingRequests = 0; // Holds the number of outstanding async requests that have not returned a response yet
        var _postManager;
        var _httpInterface;
        var _core;
        var _customHttpInterface = true;
        var _queryStringParameters = [];
        var _headers = {};
        var _batchQueue = [];
        var _serializer = null;
        var _enableEventTimings = false;
        var _cookieMgr;
        dynamicProto(HttpManager, this, function (_self) {
            var _sendCredentials = true;
            _self.initialize = function (endpointUrl, core, postChannel, httpInterface, channelConfig) {
                if (!channelConfig) {
                    channelConfig = {};
                }
                _urlString = endpointUrl + _urlString;
                _core = core;
                _cookieMgr = core.getCookieMgr();
                _enableEventTimings = !_core.config.disableEventTimings;
                var enableCompoundKey = !!_core.config.enableCompoundKey;
                _postManager = postChannel;
                var valueSanitizer = channelConfig.valueSanitizer;
                var stringifyObjects = channelConfig.stringifyObjects;
                if (!isUndefined(channelConfig.enableCompoundKey)) {
                    enableCompoundKey = !!channelConfig.enableCompoundKey;
                }
                _serializer = new Serializer(_core, valueSanitizer, stringifyObjects, enableCompoundKey);
                _httpInterface = httpInterface;
                if (!_httpInterface) {
                    _customHttpInterface = false;
                    _useBeacons = !isReactNative(); // Only use beacons if not running in React Native
                    var location_1 = getLocation();
                    if (location_1 && location_1.protocol && location_1.protocol.toLowerCase() === "file:") {
                        // Special case where a local html file fails with a CORS error on Chromium browsers
                        _sendCredentials = false;
                    }
                    var sendPostFunc = null;
                    if (useXDomainRequest()) {
                        sendPostFunc = _xdrSendPost;
                    }
                    else if (isReactNative()) {
                        sendPostFunc = _fetchSendPost;
                    }
                    else if (typeof XMLHttpRequest !== "undefined") {
                        sendPostFunc = _xhrSendPost;
                    }
                    _httpInterface = {
                        sendPOST: sendPostFunc
                    };
                }
            };
            // Special internal method to allow the DebugPlugin to hook embedded objects
            _self["_getDbgPlgTargets"] = function () {
                return [_httpInterface, _killSwitch, _serializer];
            };
            function _xdrSendPost(payload, oncomplete) {
                // It doesn't support custom headers, so no action is taken with current requestHeaders
                var xdr = new XDomainRequest();
                xdr.open(Method, payload.urlString);
                // can't get the status code in xdr.
                xdr.onload = function () {
                    // we will assume onload means the request succeeded.
                    var response = _getResponseText(xdr);
                    _doOnComplete(oncomplete, 200, {}, response);
                    _handleCollectorResponse(response);
                };
                // we will assume onerror means we need to drop the events.
                xdr.onerror = function () {
                    _doOnComplete(oncomplete, 400, {});
                };
                // we will assume ontimeout means we need to retry the events.
                xdr.ontimeout = function () {
                    _doOnComplete(oncomplete, 500, {});
                };
                xdr.send(payload.data);
            }
            function _fetchSendPost(payload, oncomplete) {
                var _a;
                fetch(payload.urlString, (_a = {
                        body: payload.data,
                        method: Method,
                        credentials: "include"
                    },
                    _a[DisabledPropertyName] = true,
                    _a.headers = payload.headers,
                    _a)).then(function (response) {
                    var headerMap = {};
                    var responseText = "";
                    if (response.headers) {
                        response.headers.forEach(function (value, name) {
                            headerMap[name] = value;
                        });
                    }
                    if (response.body) {
                        response.text().then(function (text) {
                            responseText = text;
                        });
                    }
                    _doOnComplete(oncomplete, response.status, headerMap, responseText);
                    _handleCollectorResponse(responseText);
                })["catch"](function (error) {
                    // In case there is an error in the request. Set the status to 0
                    // so that the events can be retried later.
                    _doOnComplete(oncomplete, 0, {});
                });
            }
            function _xhrSendPost(payload, oncomplete, sync) {
                function _appendHeader(theHeaders, xhr, name) {
                    if (!theHeaders[name] && xhr && xhr.getResponseHeader) {
                        var value = xhr.getResponseHeader(name);
                        if (value) {
                            theHeaders[name] = strTrim(value);
                        }
                    }
                    return theHeaders;
                }
                function _getAllResponseHeaders(xhr) {
                    var theHeaders = {};
                    if (!xhr.getAllResponseHeaders) {
                        // Firefox 2-63 doesn't have getAllResponseHeaders function but it does have getResponseHeader
                        // Only call these if getAllResponseHeaders doesn't exist, otherwise we can get invalid response errors
                        // as collector is not currently returning the correct header to allow JS to access these headers
                        theHeaders = _appendHeader(theHeaders, xhr, strTimeDeltaHeader);
                        theHeaders = _appendHeader(theHeaders, xhr, strKillDurationHeader);
                        theHeaders = _appendHeader(theHeaders, xhr, strKillDurationSecondsHeader);
                    }
                    else {
                        theHeaders = _convertAllHeadersToMap(xhr.getAllResponseHeaders());
                    }
                    return theHeaders;
                }
                function xhrComplete(xhr, responseTxt) {
                    _doOnComplete(oncomplete, xhr.status, _getAllResponseHeaders(xhr), responseTxt);
                }
                var xhRequest = new XMLHttpRequest();
                try {
                    xhRequest[DisabledPropertyName] = true;
                }
                catch (e) {
                    // If the environment has locked down the XMLHttpRequest (preventExtensions and/or freeze), this would
                    // cause the request to fail and we no telemetry would be sent
                }
                if (_sendCredentials) {
                    // Special case where a local html file fails with a CORS error on Chromium browsers (protocol is only evaluated on initialization)
                    xhRequest.withCredentials = true;
                }
                xhRequest.open(Method, payload.urlString, !sync);
                // Set custom headers (e.g. gzip) here (after open())
                objForEachKey(payload.headers, function (name, value) {
                    xhRequest.setRequestHeader(name, value);
                });
                xhRequest.onload = function () {
                    var response = _getResponseText(xhRequest);
                    xhrComplete(xhRequest, response);
                    _handleCollectorResponse(response);
                };
                xhRequest.onerror = function () {
                    xhrComplete(xhRequest);
                };
                xhRequest.ontimeout = function () {
                    xhrComplete(xhRequest);
                };
                xhRequest.send(payload.data);
            }
            function _doOnComplete(oncomplete, status, headers, response) {
                try {
                    oncomplete(status, headers, response);
                }
                catch (e) {
                    _postManager.diagLog().throwInternal(LoggingSeverity.WARNING, _ExtendedInternalMessageId.SendPostOnCompleteFailure, e);
                }
            }
            _self.addQueryStringParameter = function (name, value) {
                for (var i = 0; i < _queryStringParameters.length; i++) {
                    if (_queryStringParameters[i].name === name) {
                        _queryStringParameters[i].value = value;
                        return;
                    }
                }
                _queryStringParameters.push({ name: name, value: value });
            };
            _self.addHeader = function (name, value) {
                _headers[name] = value;
            };
            _self.canSendRequest = function () {
                return _hasIdleConnection() && _clockSkewManager.allowRequestSending();
            };
            _self.sendQueuedRequests = function (sendReason, isAsync, useSendBeacon) {
                var isSync = isNullOrUndefined(isAsync) ? false : !isAsync;
                if (_canSendPayload(_batchQueue, isSync, 0)) {
                    _sendBatches(_clearQueue(), 0, false, isSync, sendReason || 0 /* Undefined */, !!useSendBeacon);
                }
            };
            _self.isCompletelyIdle = function () {
                return !_paused && _outstandingRequests === 0 && _batchQueue.length === 0;
            };
            _self.addBatch = function (theBatch) {
                if (theBatch && theBatch.count() > 0) {
                    // Try and kill the event faster
                    if (_killSwitch.isTenantKilled(theBatch.iKey())) {
                        return false;
                    }
                    _batchQueue.push(theBatch);
                }
                return true;
            };
            /**
             * Queue all the remaining requests to be sent. The requests will be
             * sent using HTML5 Beacons if they are available.
             */
            _self.teardown = function () {
                if (_batchQueue.length > 0) {
                    _sendBatches(_clearQueue(), 0, true, true, 2 /* Unload */, true);
                }
            };
            /**
             * Pause the sending of requests. No new requests will be sent.
             */
            _self.pause = function () {
                _paused = true;
            };
            /**
             * Resume the sending of requests.
             */
            _self.resume = function () {
                _paused = false;
                _self.sendQueuedRequests(4 /* Resumed */, false);
            };
            /**
             * Sends a request synchronously to the Aria collector. This api is used to send
             * a request containing a single immediate event.
             *
             * @param batch - The request to be sent.
             * @param sendReason   - The token used to send the request.
             */
            _self.sendSynchronousBatch = function (batch, sendReason, useSendBeacon) {
                // This will not take into account the max connections restriction. Since this is sync, we can
                // only send one of this request at a time and thus should not worry about multiple connections
                // being used to send synchronous events.
                // Increment active connection since we are still going to use a connection to send the request.
                if (batch && batch.count() > 0) {
                    // For sync requests we will not wait for the clock skew.
                    _sendBatches([batch], 0, false, true, sendReason || 0 /* Undefined */, !!useSendBeacon);
                }
            };
            function _hasIdleConnection() {
                return !_paused && _outstandingRequests < maxConnections;
            }
            function _clearQueue() {
                var theQueue = _batchQueue;
                _batchQueue = [];
                return theQueue;
            }
            function _canSendPayload(theBatches, isSync, retryCnt) {
                var result = false;
                if (theBatches && theBatches.length > 0 && !_paused && _httpInterface && _serializer) {
                    // Always attempt to send synchronous events don't wait for idle or clockSkew
                    // and don't block retry requests if clockSkew is not yet set
                    result = isSync || (_hasIdleConnection() && (retryCnt > 0 || _clockSkewManager.allowRequestSending()));
                }
                return result;
            }
            function _createDebugBatches(theBatches) {
                var values = {};
                if (theBatches) {
                    arrForEach(theBatches, function (theBatch, idx) {
                        values[idx] = {
                            iKey: theBatch.iKey(),
                            evts: theBatch.events()
                        };
                    });
                }
                return values;
            }
            function _sendBatches(theBatches, retryCount, isTeardown, isSynchronous, sendReason, useSendBeacon) {
                if (!theBatches || theBatches.length === 0) {
                    // Nothing to do
                    return;
                }
                if (_paused) {
                    _sendBatchesNotification(theBatches, 1 /* Paused */, isSynchronous);
                    return;
                }
                var orgBatches = theBatches;
                doPerf(_core, function () { return "HttpManager:_sendBatches"; }, function (perfEvt) {
                    if (perfEvt) {
                        // Perf Monitoring is enabled, so create a "Quick" copy of the original batches so we still report
                        // the original values as part of the perfEvent. This is because theBatches uses .shift() to remove each
                        // batch as they are processed - removing from the original array, so by the time the _createDebugBatches()
                        // function is called the passed in value has changed and therefore the reported value for the perfEvent is incorrect
                        theBatches = theBatches.slice(0);
                    }
                    var droppedBatches = [];
                    var thePayload = null;
                    var serializationStart = getTime();
                    while (_canSendPayload(theBatches, isSynchronous, retryCount)) {
                        var theBatch = theBatches.shift();
                        if (theBatch && theBatch.count() > 0) {
                            if (!_killSwitch.isTenantKilled(theBatch.iKey())) {
                                // Make sure we have a payload object
                                thePayload = thePayload || _serializer.createPayload(retryCount, isTeardown, isSynchronous, useSendBeacon && _canUseSendBeaconApi());
                                // Add the batch to the current payload
                                if (!_serializer.appendPayload(thePayload, theBatch, maxEventsPerBatch)) {
                                    // Entire batch was not added so send the payload and retry adding this batch
                                    _doPayloadSend(thePayload, serializationStart, getTime(), sendReason);
                                    serializationStart = getTime();
                                    theBatches = [theBatch].concat(theBatches);
                                    thePayload = null;
                                }
                                else if (thePayload.overflow !== null) {
                                    // Total Payload size was exceeded so send the payload and add the unsent as the next batch to send
                                    theBatches = [thePayload.overflow].concat(theBatches);
                                    thePayload.overflow = null;
                                    _doPayloadSend(thePayload, serializationStart, getTime(), sendReason);
                                    serializationStart = getTime();
                                    thePayload = null;
                                }
                            }
                            else {
                                droppedBatches.push(theBatch);
                            }
                        }
                    }
                    // Make sure to flush any remaining payload
                    if (thePayload) {
                        _doPayloadSend(thePayload, serializationStart, getTime(), sendReason);
                    }
                    if (theBatches.length > 0) {
                        // Add any unsent batches back to the head of the queue
                        _batchQueue = theBatches.concat(_batchQueue);
                    }
                    // Now send notification about any dropped events
                    _sendBatchesNotification(droppedBatches, 8004 /* KillSwitch */, isSynchronous);
                }, function () { return ({ batches: _createDebugBatches(orgBatches), retryCount: retryCount, isTeardown: isTeardown, isSynchronous: isSynchronous, sendReason: sendReason, useSendBeacon: useSendBeacon }); }, !isSynchronous);
            }
            function _buildQueryString(thePayload) {
                var urlString = _urlString;
                var apiQsKeys = "";
                arrForEach(thePayload.apiKeys, function (apiKey) {
                    if (apiQsKeys.length > 0) {
                        apiQsKeys += ",";
                    }
                    apiQsKeys += apiKey;
                });
                if (apiQsKeys.length > 0) {
                    urlString += "&apikey=" + apiQsKeys;
                }
                urlString += "&upload-time=" + dateNow().toString();
                var msfpc = _getMsfpc(thePayload);
                if (isValueAssigned(msfpc)) {
                    urlString = urlString + "&ext.intweb.msfpc=" + msfpc;
                }
                if (_clockSkewManager.shouldAddClockSkewHeaders()) {
                    urlString += "&time-delta-to-apply-millis=" + _clockSkewManager.getClockSkewHeaderValue();
                }
                if (_core.getWParam) {
                    var wParam = _core.getWParam();
                    if (wParam >= 0) {
                        urlString += "&w=" + wParam;
                    }
                }
                for (var i = 0; i < _queryStringParameters.length; i++) {
                    urlString += "&" + _queryStringParameters[i].name + "=" + _queryStringParameters[i].value;
                }
                return urlString;
            }
            function _canUseSendBeaconApi() {
                return !_customHttpInterface && _useBeacons && isBeaconsSupported();
            }
            function _setTimingValue(timings, name, value) {
                timings[name] = timings[name] || {};
                timings[name][_postManager.identifier] = value;
            }
            function _doPayloadSend(thePayload, serializationStart, serializationCompleted, sendReason) {
                if (thePayload && thePayload.payloadBlob && thePayload.payloadBlob.length > 0) {
                    var urlString_1 = _buildQueryString(thePayload);
                    var sendEventStart_1 = getTime();
                    var strSendAttempt_1 = "sendAttempt";
                    doPerf(_core, function () { return "HttpManager:_doPayloadSend"; }, function () {
                        // Increment the send attempt count and add timings after packaging (So it's not serialized in the 1st attempt)
                        for (var batchLp = 0; batchLp < thePayload.batches.length; batchLp++) {
                            var theBatch = thePayload.batches[batchLp];
                            var theEvents = theBatch.events();
                            for (var evtLp = 0; evtLp < theEvents.length; evtLp++) {
                                var telemetryItem = theEvents[evtLp];
                                if (_enableEventTimings) {
                                    var timings = telemetryItem.timings = telemetryItem.timings || {};
                                    _setTimingValue(timings, "sendEventStart", sendEventStart_1);
                                    _setTimingValue(timings, "serializationStart", serializationStart);
                                    _setTimingValue(timings, "serializationCompleted", serializationCompleted);
                                }
                                telemetryItem[strSendAttempt_1] > 0 ? telemetryItem[strSendAttempt_1]++ : telemetryItem[strSendAttempt_1] = 1;
                            }
                        }
                        // Note: always sending this notification in a synchronous manner.
                        _sendBatchesNotification(thePayload.batches, (1000 /* SendingUndefined */ + (sendReason || 0 /* Undefined */)), thePayload.isSync, true);
                        var orgPayloadData = {
                            data: thePayload.payloadBlob,
                            urlString: urlString_1,
                            headers: _headers
                        };
                        var sender = null;
                        var useSendHook = !!_self.sendHook;
                        // Send all data using beacon if closing mode is on or channel was teared down
                        if (_canUseSendBeaconApi() && thePayload.isBeacon) {
                            useSendHook = false;
                            sender = function (payload) {
                                _sendUsingBeacons(payload, thePayload, sendReason);
                                // Ignore any processed data and just send the original content as we can't pass any custom headers via sendBeacon
                                if (_self.sendListener) {
                                    _self.sendListener(orgPayloadData, payload, thePayload.isSync || thePayload.isTeardown, true);
                                }
                            };
                        }
                        else if (_httpInterface) {
                            // Send sync requests if the request is immediate or we are tearing down telemetry.
                            sender = function (payload) {
                                // Notify the clock skew manager that we are sending the first request (Potentially blocking all further requests)
                                _clockSkewManager.firstRequestSent();
                                var onComplete = function (status, headers) {
                                    _retryRequestIfNeeded(status, headers, thePayload, sendReason);
                                };
                                try {
                                    _httpInterface.sendPOST(payload, onComplete, thePayload.isTeardown || thePayload.isSync);
                                    if (_self.sendListener) {
                                        // Send the original payload to the listener
                                        _self.sendListener(orgPayloadData, payload, thePayload.isSync || thePayload.isTeardown, false);
                                    }
                                }
                                catch (ex) {
                                    _doOnComplete(onComplete, 0, {});
                                }
                            };
                        }
                        doPerf(_core, function () { return "HttpManager:_doPayloadSend.sender"; }, function () {
                            if (sender) {
                                if (!thePayload.isSync) {
                                    _outstandingRequests++;
                                }
                                if (useSendHook) {
                                    // Create a new IPayloadData that is sent into the hook method, so that the hook method
                                    // can't change the object references to the orgPayloadData (it can still change the content -- mainly the headers)
                                    var hookData_1 = {
                                        data: orgPayloadData.data,
                                        urlString: orgPayloadData.urlString,
                                        headers: orgPayloadData.headers
                                    };
                                    var senderCalled_1 = false;
                                    doPerf(_core, function () { return "HttpManager:_doPayloadSend.sendHook"; }, function () {
                                        try {
                                            _self.sendHook(hookData_1, function (payload) {
                                                senderCalled_1 = true;
                                                sender(payload);
                                            }, thePayload.isSync || thePayload.isTeardown);
                                        }
                                        catch (ex) {
                                            if (!senderCalled_1) {
                                                // The hook never called the sender -- assume that it never will
                                                sender(orgPayloadData);
                                            }
                                        }
                                    });
                                }
                                else {
                                    sender(orgPayloadData);
                                }
                            }
                        });
                    }, function () { return ({ thePayload: thePayload, serializationStart: serializationStart, serializationCompleted: serializationCompleted, sendReason: sendReason }); }, thePayload.isSync);
                }
                // Ensure that we send any discard events for oversize events even when there was no payload to send
                _sendBatchesNotification(thePayload.sizeExceed, 8003 /* SizeLimitExceeded */, thePayload.isSync);
            }
            function _sendUsingBeacons(payload, thePayload, sendReason) {
                // Custom headers not supported in sendBeacon _headers would be ignored
                try {
                    if (isBeaconsSupported()) {
                        var nav_1 = getNavigator();
                        if (nav_1.sendBeacon(payload.urlString, payload.data)) {
                            // Request successfully sent via beacon.
                            _handleRequestFinished(thePayload, 200 /* Complete */, sendReason, false);
                            return;
                        }
                        else {
                            // Split data and try to send as much events as possible
                            var droppedBatches_1 = null;
                            arrForEach(thePayload.batches, function (theBatch) {
                                if (droppedBatches_1 && theBatch && theBatch.count() > 0) {
                                    var theEvents = theBatch.events();
                                    for (var lp = 0; lp < theEvents.length; lp++) {
                                        if (!nav_1.sendBeacon(payload.urlString, _serializer.getEventBlob(theEvents[lp]))) {
                                            // Can't send anymore, so split the batch and drop the rest
                                            droppedBatches_1.push(theBatch.split(lp));
                                            break;
                                        }
                                    }
                                }
                                else {
                                    // Remove all of the events from the existing batch in the payload as the copy includes the original
                                    droppedBatches_1.push(theBatch.split(0));
                                }
                            });
                            _handleRequestFinished(thePayload, 200 /* Complete */, sendReason, false);
                            _sendBatchesNotification(droppedBatches_1, 8003 /* SizeLimitExceeded */, thePayload.isSync, true);
                        }
                    }
                }
                catch (ex) {
                    _postManager.diagLog().warnToConsole("Failed to send telemetry using sendBeacon API. Ex:" + ex);
                }
            }
            function _addEventCompletedTimings(theEvents, sendEventCompleted) {
                if (_enableEventTimings) {
                    arrForEach(theEvents, function (theEvent) {
                        var timings = theEvent.timings = theEvent.timings || {};
                        _setTimingValue(timings, "sendEventCompleted", sendEventCompleted);
                    });
                }
            }
            function _retryRequestIfNeeded(status, headers, thePayload, sendReason) {
                var reason = 9000 /* ResponseFailure */;
                var droppedBatches = null;
                var isRetrying = false;
                var backOffTrans = false;
                try {
                    var shouldRetry = true;
                    if (typeof status !== strUndefined) {
                        if (headers) {
                            _clockSkewManager.setClockSkew(headers[strTimeDeltaHeader]);
                            var killDuration = headers[strKillDurationHeader] || headers["kill-duration-seconds"];
                            arrForEach(_killSwitch.setKillSwitchTenants(headers[strKillTokensHeader], killDuration), function (killToken) {
                                arrForEach(thePayload.batches, function (theBatch) {
                                    if (theBatch.iKey() === killToken) {
                                        // Make sure we have initialized the array
                                        droppedBatches = droppedBatches || [];
                                        // Create a copy of the batch with all of the events (and more importantly the action functions)
                                        var removedEvents = theBatch.split(0);
                                        // And then remove the events for the payload batch and reduce the actual number of processed
                                        thePayload.numEvents -= removedEvents.count();
                                        droppedBatches.push(removedEvents);
                                    }
                                });
                            });
                        }
                        if (status === 200) {
                            // Response was successfully sent
                            reason = 200 /* Complete */;
                            return;
                        }
                        if (!RetryPolicy.shouldRetryForStatus(status) || thePayload.numEvents <= 0) {
                            // Only retry for specific response codes and if there is still events after kill switch processing
                            shouldRetry = false;
                        }
                        // Derive the notification response from the HttpStatus Code
                        reason = 9000 /* ResponseFailure */ + (status % 1000);
                    }
                    if (shouldRetry) {
                        // The events should be retried -- so change notification to requeue them
                        reason = 100 /* RequeueEvents */;
                        var retryCount_1 = thePayload.retryCnt;
                        if (!thePayload.isSync) {
                            if (retryCount_1 < maxRetries) {
                                isRetrying = true;
                                _doAction(function () {
                                    // try to resend the same batches
                                    if (!thePayload.isSync) {
                                        // Reduce the outstanding request count (if this was an async request) as we didn't reduce the count
                                        // previously and we are about to reschedule our retry attempt and we want an attempt to send
                                        // to occur, it's also required to ensure that a follow up handleRequestFinished() call occurs
                                        _outstandingRequests--;
                                    }
                                    _sendBatches(thePayload.batches, retryCount_1 + 1, thePayload.isTeardown, thePayload.isSync, 5 /* SendRequestReason.Retry */, thePayload.isBeacon);
                                }, true, RetryPolicy.getMillisToBackoffForRetry(retryCount_1));
                            }
                            else {
                                backOffTrans = true;
                            }
                        }
                    }
                }
                finally {
                    if (!isRetrying) {
                        // Make sure the clockSkewManager doesn't blocking further sending of requests once we have a proper response
                        // This won't override any previously sent clock Skew value
                        _clockSkewManager.setClockSkew();
                        _handleRequestFinished(thePayload, reason, sendReason, backOffTrans);
                    }
                    _sendBatchesNotification(droppedBatches, 8004 /* KillSwitch */, thePayload.isSync);
                }
            }
            function _handleRequestFinished(thePayload, batchReason, sendReason, backOffTrans) {
                try {
                    if (backOffTrans) {
                        // Slow down the transmission requests
                        _postManager._backOffTransmission();
                    }
                    if (batchReason === 200 /* Complete */) {
                        if (!backOffTrans && !thePayload.isSync) {
                            // We have a successful async response, so the lets open the floodgates
                            // The reason for checking isSync is to avoid unblocking if beacon send occurred as it
                            // doesn't wait for a response.
                            _postManager._clearBackOff();
                        }
                        _addCompleteTimings(thePayload.batches);
                    }
                    // Send the notifications synchronously
                    _sendBatchesNotification(thePayload.batches, batchReason, thePayload.isSync, true);
                }
                finally {
                    if (!thePayload.isSync) {
                        // we always need to decrement this value otherwise the httpmanager locks up and won't send any more events
                        _outstandingRequests--;
                        // Don't try to send additional queued events if this is a retry operation as the retried
                        // response will eventually call _handleRequestFinished for the retried event
                        if (sendReason !== 5 /* Retry */) {
                            // Try and send any other queued batched events
                            _self.sendQueuedRequests(sendReason, !thePayload.isSync, thePayload.isBeacon);
                        }
                    }
                }
            }
            function _addCompleteTimings(theBatches) {
                if (_enableEventTimings) {
                    var sendEventCompleted_1 = getTime();
                    arrForEach(theBatches, function (theBatch) {
                        if (theBatch && theBatch.count() > 0) {
                            _addEventCompletedTimings(theBatch.events(), sendEventCompleted_1);
                        }
                    });
                }
            }
            function _doAction(cb, isSync, interval) {
                if (isSync) {
                    cb();
                }
                else {
                    _postManager._setTimeoutOverride(cb, interval);
                }
            }
            /**
            * Converts the XHR getAllResponseHeaders to a map containing the header key and value.
            */
            // tslint:disable-next-line: align
            function _convertAllHeadersToMap(headersString) {
                var headers = {};
                if (isString(headersString)) {
                    var headersArray = strTrim(headersString).split(/[\r\n]+/);
                    arrForEach(headersArray, function (headerEntry) {
                        if (headerEntry) {
                            var idx = headerEntry.indexOf(": ");
                            if (idx !== -1) {
                                // The new spec has the headers returning all as lowercase -- but not all browsers do this yet
                                var header = strTrim(headerEntry.substring(0, idx)).toLowerCase();
                                var value = strTrim(headerEntry.substring(idx + 1));
                                headers[header] = value;
                            }
                            else {
                                headers[strTrim(headerEntry)] = 1;
                            }
                        }
                    });
                }
                return headers;
            }
            function _getMsfpc(thePayload) {
                for (var lp = 0; lp < thePayload.batches.length; lp++) {
                    var batchEvents = thePayload.batches[lp].events();
                    for (var evtLp = 0; evtLp < batchEvents.length; evtLp++) {
                        var intWeb = ((batchEvents[evtLp].ext || {})["intweb"] || {});
                        if (isValueAssigned(intWeb["msfpc"])) {
                            return encodeURIComponent(intWeb["msfpc"]);
                        }
                    }
                }
                return "";
            }
            function _handleCollectorResponse(responseText) {
                var responseHandlers = _self._responseHandlers;
                try {
                    for (var i = 0; i < responseHandlers.length; i++) {
                        try {
                            responseHandlers[i](responseText);
                        }
                        catch (e) {
                            _postManager.diagLog().throwInternal(LoggingSeverity.CRITICAL, _ExtendedInternalMessageId.PostResponseHandler, "Response handler failed: " + e);
                        }
                    }
                    if (responseText) {
                        var response = JSON.parse(responseText);
                        if (isValueAssigned(response.webResult) && isValueAssigned(response.webResult.msfpc)) {
                            // Set cookie
                            _cookieMgr.set("MSFPC", response.webResult.msfpc, 365 * 86400);
                        }
                    }
                }
                catch (ex) {
                    // Doing nothing
                }
            }
            function _sendBatchesNotification(theBatches, batchReason, isSyncRequest, sendSync) {
                if (theBatches && theBatches.length > 0 && actions) {
                    var theAction_1 = actions[_getNotificationAction(batchReason)];
                    if (theAction_1) {
                        doPerf(_core, function () { return "HttpManager:_sendBatchesNotification"; }, function () {
                            _doAction(function () {
                                try {
                                    theAction_1.call(actions, theBatches, batchReason, isSyncRequest);
                                }
                                catch (e) {
                                    _postManager.diagLog().throwInternal(LoggingSeverity.CRITICAL, _ExtendedInternalMessageId.NotificationException, "send request notification failed: " + e);
                                }
                            }, sendSync || isSyncRequest, 0);
                        }, function () { return ({ batches: _createDebugBatches(theBatches), reason: batchReason, isSync: isSyncRequest, sendSync: sendSync }); }, !isSyncRequest);
                    }
                }
            }
            function _getNotificationAction(reason) {
                var action = _eventActionMap[reason];
                if (!isValueAssigned(action)) {
                    action = strOther;
                    if (reason >= 9000 /* ResponseFailure */ && reason <= 9999 /* ResponseFailureMax */) {
                        action = strResponseFail;
                    }
                    else if (reason >= 8000 /* EventsDropped */ && reason <= 8999 /* EventsDroppedMax */) {
                        action = strDropped;
                    }
                    else if (reason >= 1000 /* SendingUndefined */ && reason <= 1999 /* SendingEventMax */) {
                        action = strSending;
                    }
                }
                return action;
            }
        });
    }
    /**
     * @constructor
     * @param requestQueue   - The queue that contains the requests to be sent.
     * @param endpointUrl   - The collector url to which the requests must be sent.
     * @param postManager   - The post manager that we should add requests back to if needed.
     * @param httpInterface - The http interface that should be used to send HTTP requests.
     * @param channelConfig - The IChannelConfiguration the should be used to get additional configuration
     */
    HttpManager.prototype.initialize = function (endpointUrl, core, postChannel, httpInterface, channelConfig) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
    };
    /**
     * Add query string parameter to url
     * @param name   - Query string name.
     * @param value  - Query string value.
     */
    HttpManager.prototype.addQueryStringParameter = function (name, value) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
    };
    /**
     * Add header to request
     * @param name   - Header name.
     * @param value  - Header value.
     */
    HttpManager.prototype.addHeader = function (name, value) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
    };
    /**
     * Add the batch of events to the queue for sending
     * @param batch The batch with the events to send
     * @returns True if the http manager has accepted the batch (including if the batch is empty) otherwise false
     */
    HttpManager.prototype.addBatch = function (batch) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return false;
    };
    /**
     * Check if there is an idle connection and we can send a request.
     * @returns True if there is an idle connection, false otherwise.
     */
    HttpManager.prototype.canSendRequest = function () {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return false;
    };
    /**
     * Send requests in the request queue up if there is an idle connection, sending is
     * not pause and clock skew manager allows sending request.
     * @param sendReason   - The reason the batch is being sent
     * @param usAsync - Should the queued batches be sent as an async request. Defaults: true
     * @param useSendBeacon - Try to send the payload using the sendBeacon API is it available and enabled. Defaults: false
     */
    HttpManager.prototype.sendQueuedRequests = function (sendReason, isAsync, useSendBeacon) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
    };
    /**
     * Check if there are no active requests being sent.
     * @returns True if idle, false otherwise.
     */
    HttpManager.prototype.isCompletelyIdle = function () {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return false;
    };
    /**
     * Queue all the remaining requests to be sent. The requests will be
     * sent using HTML5 Beacons if they are available.
     */
    HttpManager.prototype.teardown = function () {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
    };
    /**
     * Pause the sending of requests. No new requests will be sent.
     */
    HttpManager.prototype.pause = function () {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
    };
    /**
     * Resume the sending of requests.
     */
    HttpManager.prototype.resume = function () {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
    };
    /**
     * Sends the batches synchronously to the collector. This api is used to send a batches immediate event.
     *
     * @param batch - The batch of events to be sent.
     * @param sendReason   - The reason the batch is being sent
     * @param useSendBeacon - Try to send the payload using the sendBeacon API is it available and enabled. Defaults: false
     */
    HttpManager.prototype.sendSynchronousBatch = function (batch, sendReason, useSendBeacon) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
    };
    return HttpManager;
}());
export default HttpManager;
//# sourceMappingURL=HttpManager.js.map